import React, { useContext, FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

// MUI
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

// Custom Components
import useFormController from '@/components/useFormController';
import DynamicDialog from '@/ui/Dialog/DynamicDialog';
import DialogToolbarHeading from '@/ui/Dialog/DialogToolbarHeading';
import DialogToolbarButtonClose from '@/ui/Dialog/ToolbarButtons/DialogToolbarButtonClose';
import DialogToolbarMenuButton from '@/ui/Dialog/DialogToolbarMenuButton';
import DialogToolbarMenuItemDelete from '@/ui/Dialog/ToolbarMenuItems/DialogToolbarMenuItemDelete';
import DialogToolbarMenuItemRestoreDeleted from '@/ui/Dialog/ToolbarMenuItems/DialogToolbarMenuItemRestoreDeleted';
import ToolbarFillContent from '@/ui/Toolbar/ToolbarFillContent';
import DialogActionButtonClose from '@/ui/Dialog/ActionButtons/DialogActionButtonClose';
import DialogActionButtonAdd from '@/ui/Dialog/ActionButtons/DialogActionButtonAdd';
import DialogActionButtonSave from '@/ui/Dialog/ActionButtons/DialogActionButtonSave';
import DialogContext, { IDialogProps } from '@/context/DialogContext/DialogContext';
import SnackbarContext from '@/context/SnackbarContext/SnackbarContext';
import { GridContainer } from '@/ui/Grid';
import FormContent from '@/components/FormContent';
import DialogInfoMeta from '@/ui/Dialog/DialogInfoMeta';

// Types
import { ClosingDetails, FormControllerProps } from '@/@types/components/formController';

const GeneralGrobljaDialog: FunctionComponent<IDialogProps> = (props) => {
  const { hideDialog } = useContext(DialogContext);
  const { showNotification } = useContext(SnackbarContext);
  const { t } = useTranslation();

  const { dc, mode, form, onClose, title, level, columns = 2, allowDelete } = props;

  const { record, validation, fields, pickerDataControllers, onFieldChange, doInsert, doUpdate, doClose, doDelete, doRestore } =
    useFormController(props as FormControllerProps);

  const handleFieldChange = (value: any, source: string) => {
    if (onFieldChange) {
      onFieldChange(value, source);
    }
  };

  const handleInsert = () => {
    if (doInsert) {
      doInsert()
        .then((result) => {
          if (result.success) {
            const msg = t('messages.addRecord');
            showNotification(msg, 'success');
            close({ dataChanged: true, action: 'insert' });
          } else if (result.error) {
            const msg = t('error.addRecord');
            showNotification(msg, 'error');
          } else if (result.validationPass === false) {
            // do nothing
          }
        })
        .catch(() => {
          const msg = t('error.addRecord');
          showNotification(msg, 'error');
        });
    }
  };

  const handleUpdate = () => {
    if (doUpdate) {
      doUpdate()
        .then((result) => {
          if (result.success) {
            const msg = t('messages.updateRecord');
            showNotification(msg, 'success');
            close({ dataChanged: true, action: 'update' });
          } else if (result.error) {
            const msg = `${t('error.updateRecord')}`;
            showNotification(msg, 'error');
          } else if (result.validationPass === false) {
            // do nothing
          }
        })
        .catch(() => {
          const msg = `${t('error.updateRecord')}`;
          showNotification(msg, 'error');
        });
    }
  };

  const handleClose = () => {
    if (doClose) {
      doClose()
        .then((resolve) => {
          if (resolve.success) {
            // close
            close({ dataChanged: false, action: 'cancel' });
          }
        })
        .catch((e) => console.error(e));
    }
  };

  const close = (result: ClosingDetails) => {
    onClose(result);
    hideDialog(level);
  };

  const handleDelete = () => {
    if (doDelete) {
      doDelete()
        .then((result) => {
          if (result.success) {
            const msg = t('messages.deleteRecord');
            showNotification(msg, 'success');
            close({ dataChanged: true, action: 'update' });
          } else if (result.error) {
            const msg = t('error.deleteRecord');
            showNotification(msg, 'error');
          }
        })
        .catch(() => {
          const msg = t('error.deleteRecord');
          showNotification(msg, 'error');
        });
    }
  };

  const handleRestore = () => {
    if (doRestore) {
      doRestore()
        .then((result) => {
          if (result.success) {
            const msg = t('messages.restoreRecord');
            showNotification(msg, 'success');
            close({ dataChanged: true, action: 'update' });
          } else if (result.error) {
            const msg = t('error.restore');
            showNotification(msg, 'error');
          }
        })
        .catch(() => {
          const msg = t('error.restore');
          showNotification(msg, 'error');
        });
    }
  };
  let cmode = mode;
  if (record.is_deleted) {
    cmode = 'view';
  }
  return (
    <DynamicDialog open maxWidth="md" onClose={handleClose}>
      <DialogTitle>
        <Toolbar variant="dense" disableGutters>
          <DialogToolbarHeading>{t(title)}</DialogToolbarHeading>
          <ToolbarFillContent />
            {mode === 'update' && ((record.is_deleted || allowDelete === false) && (
                <DialogToolbarMenuButton>
                  {record.is_deleted ? (
                    <DialogToolbarMenuItemRestoreDeleted onClick={handleRestore} />
                  ) : (
                    <DialogToolbarMenuItemDelete onClick={handleDelete} />
                  )}
                </DialogToolbarMenuButton>
              )
            )}
          <DialogToolbarButtonClose onClick={handleClose} />
        </Toolbar>
      </DialogTitle>
      <DialogContent>
        <GridContainer my={2}>
          <FormContent
            fieldNames={dc.getFormFieldsNames(form || mode)}
            record={record}
            validation={validation}
            fields={fields}
            layout={dc.getForm(form || mode)?.layout}
            onFieldChange={handleFieldChange}
            mode={cmode}
            columns={columns}
            pickerDataControllers={pickerDataControllers}
            geometryChangeType="Polygon"
            mapId={46}
          />
        </GridContainer>
      </DialogContent>
      <DialogActions>
        <Box sx={{ flexGrow: 1 }}>
          <DialogInfoMeta record={record} />
        </Box>
        <DialogActionButtonClose variant="outlined" onClick={handleClose} />
        {mode === 'insert' ? (
          <DialogActionButtonAdd variant="contained" onClick={handleInsert} />
        ) : mode === 'update' && record.is_deleted !== true ? (
          <DialogActionButtonSave variant="contained" onClick={handleUpdate} />
        ) : null}
      </DialogActions>
    </DynamicDialog>
  );
};

export default GeneralGrobljaDialog;
